import type { TFunction } from 'i18next';
import type { CountryCode } from 'libphonenumber-js';
import { normalizeNamePart, isValidPhone, isPhoneReadyForValidityCheck } from '@minibook/shared';
import { LanguageSwitcher } from '../../../app/components/LanguageSwitcher.js';
import { AppIcon } from '../../ui/Icon';
import { PhoneInput } from '../../components/PhoneInput.js';
import { parseVkUserId } from '../bookingSlotUtils.js';

type ClientBirthDate = { day: string; month: string; year: string };

type BookingStep3ClientProps = {
  t: TFunction;
  firstName: string;
  lastName: string;
  clientPhoneE164: string;
  effectiveClientEmail: string;
  lockedClientEmail: string | null;
  comment: string;
  vkId: string;
  vkPhoto: string | null;
  loadingVkData: boolean;
  clientGender: 'male' | 'female' | '';
  clientBirthDate: ClientBirthDate;
  nameError: string | null;
  emailError: string | null;
  phoneWarning: string | null;
  bookingPhoneTouched: boolean;
  bookingPhoneCountry: CountryCode;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onPhoneBlur: () => void;
  onEmailChange: (value: string) => void;
  onCommentChange: (value: string) => void;
  onVkIdChange: (value: string) => void;
  onFetchVkData: () => void;
  onGenderChange: (value: 'male' | 'female' | '') => void;
  onBirthDateChange: (value: ClientBirthDate) => void;
  onNameBlur: () => void;
  onEmailBlur: () => void;
  onSplitName: () => void;
  onClearNameError: () => void;
  onClearEmailError: () => void;
};

export function BookingStep3Client({
  t,
  firstName,
  lastName,
  clientPhoneE164,
  effectiveClientEmail,
  lockedClientEmail,
  comment,
  vkId,
  vkPhoto,
  loadingVkData,
  clientGender,
  clientBirthDate,
  nameError,
  emailError,
  phoneWarning,
  bookingPhoneTouched,
  bookingPhoneCountry,
  onBack,
  onSubmit,
  onFirstNameChange,
  onLastNameChange,
  onPhoneChange,
  onPhoneBlur,
  onEmailChange,
  onCommentChange,
  onVkIdChange,
  onFetchVkData,
  onGenderChange,
  onBirthDateChange,
  onNameBlur,
  onEmailBlur,
  onSplitName,
  onClearNameError,
  onClearEmailError,
}: BookingStep3ClientProps) {
  const firstTokens = firstName.trim().split(/\s+/).filter(Boolean);
  const lastNorm = normalizeNamePart(lastName);
  const splitSuggest = firstTokens.length >= 2 && !lastNorm;
  const splitWarning = firstTokens.length >= 2 && !!lastNorm;

  const rawPhone = clientPhoneE164.trim();
  const showPhoneInvalid =
    rawPhone.length > 0 &&
    !isValidPhone(rawPhone) &&
    (bookingPhoneTouched || isPhoneReadyForValidityCheck(rawPhone, bookingPhoneCountry));

  return (
    <div>
      <div
        className="booking-p-md-lg booking-gap-md booking-border-b"
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'var(--color-card)',
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className="booking-icon-ghost"
          onClick={onBack}
          style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}
        >
          <AppIcon name="chevronLeft" size={24} />
        </button>
        <h1 className="booking-heading" style={{ margin: 0, flex: 1, textAlign: 'left' }}>
          {t('booking.client.title')}
        </h1>
        <LanguageSwitcher />
      </div>

      <form
        id="booking-client-form"
        onSubmit={onSubmit}
        className="booking-form-padding"
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          const target = e.target as Node;
          if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            target instanceof HTMLSelectElement
          ) {
            e.preventDefault();
          }
        }}
      >
        {false && (
          <div className="booking-form-group">
            <label className="booking-label">💬 {t('booking.client.vkIdLabel')}</label>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 'var(--radius-md)',
                  backgroundImage: vkPhoto ? `url(${vkPhoto})` : undefined,
                  backgroundColor: vkPhoto
                    ? undefined
                    : firstName || lastName
                      ? 'var(--color-primary-light)'
                      : 'var(--color-muted-light)',
                  backgroundSize: vkPhoto ? 'cover' : undefined,
                  backgroundPosition: vkPhoto ? 'center' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 19,
                  fontWeight: 600,
                  color: vkPhoto
                    ? undefined
                    : firstName || lastName
                      ? 'var(--color-on-primary-container)'
                      : 'var(--color-muted)',
                  flexShrink: 0,
                  border: '1px solid var(--color-border)',
                }}
              >
                {!vkPhoto &&
                  ((firstName || lastName) ? (
                    (firstName.trim() || lastName.trim()).charAt(0).toUpperCase()
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 64 64" aria-hidden="true" style={{ color: 'currentColor' }}>
                      <circle cx="32" cy="24" r="12" fill="currentColor" />
                      <path d="M12 56c0-11 9-20 20-20s20 9 20 20" fill="currentColor" />
                    </svg>
                  ))}
              </div>
              <div style={{ flex: 1, display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-start' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={vkId}
                  onChange={(e) => onVkIdChange(e.target.value)}
                  placeholder={t('booking.client.vkIdPlaceholder')}
                  className="booking-input"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={onFetchVkData}
                  disabled={!parseVkUserId(vkId) || loadingVkData}
                  className="booking-button"
                  style={{
                    width: 48,
                    height: 48,
                    minWidth: 48,
                    minHeight: 48,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    borderRadius: 'var(--radius-md)',
                  }}
                  title={t('booking.client.fetchFromVk')}
                >
                  +
                </button>
              </div>
            </div>
            {loadingVkData && (
              <div className="booking-text-xs booking-color-muted booking-mt-xs">{t('booking.loading.slots')}</div>
            )}
          </div>
        )}

        <div className="booking-form-group">
          <label className="booking-label" style={{ display: 'block', marginBottom: 4 }}>
            {t('booking.client.name')} <span className="required">*</span>
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => {
              onFirstNameChange(e.target.value);
              if (nameError) onClearNameError();
            }}
            onBlur={onNameBlur}
            className="booking-input"
            style={{ width: '100%', marginBottom: 'var(--spacing-md)' }}
          />
          <label className="booking-label" style={{ display: 'block', marginBottom: 4 }}>
            {t('booking.client.surname')} <span className="required">*</span>
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => {
              onLastNameChange(e.target.value);
              if (nameError) onClearNameError();
            }}
            onBlur={onNameBlur}
            className="booking-input"
            style={{ width: '100%', marginBottom: 'var(--spacing-md)' }}
          />
          {nameError && (
            <div className="booking-text-xs booking-mt-xs" style={{ color: 'var(--color-danger)' }}>
              ⚠️ {nameError}
            </div>
          )}
          {(splitSuggest || splitWarning) && (
            <div
              className="booking-text-xs booking-mt-xs"
              style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-sm)' }}
            >
              {splitSuggest && t('booking.client.splitNameSuggest')}
              {splitWarning && t('booking.client.splitNameWarning')}{' '}
              <button
                type="button"
                onClick={onSplitName}
                className="booking-button"
                style={{ marginLeft: 4, padding: '2px 8px', fontSize: 'inherit' }}
              >
                {t('booking.client.splitNameAction')}
              </button>
            </div>
          )}
          <label className="booking-label" style={{ display: 'block', marginBottom: 4 }}>
            {t('booking.client.phone')} <span className="required">*</span>
          </label>
          <div style={{ marginBottom: 'var(--spacing-md)' }}>
            <PhoneInput value={clientPhoneE164} onChange={onPhoneChange} onBlur={onPhoneBlur} />
          </div>
          {phoneWarning && (
            <div
              className="booking-text-xs booking-mt-xs"
              style={{
                color: 'var(--color-warning)',
                padding: 'var(--spacing-sm)',
                background: 'var(--color-warning-bg)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              ⚠️ {phoneWarning}
            </div>
          )}
          {showPhoneInvalid && (
            <div className="booking-text-xs booking-mt-xs" style={{ color: 'var(--color-danger)' }}>
              ⚠️ {t('booking.error.invalidPhone')}
            </div>
          )}
        </div>

        <div className="booking-form-group">
          <label className="booking-label" style={{ display: 'block', marginBottom: 4 }}>
            {t('booking.client.emailOptionalLabel')}
          </label>
          <input
            type="email"
            inputMode="email"
            value={effectiveClientEmail}
            onChange={(e) => {
              if (lockedClientEmail) return;
              onEmailChange(e.target.value);
              if (emailError) onClearEmailError();
            }}
            onBlur={onEmailBlur}
            placeholder={lockedClientEmail ? '' : t('booking.client.emailPlaceholder')}
            className="booking-input"
            disabled={!!lockedClientEmail}
          />
          <div className="booking-text-xs booking-mt-xs" style={{ color: 'var(--color-text-muted)' }}>
            {lockedClientEmail ? t('booking.client.emailLockedHint') : t('booking.client.emailHint')}
          </div>
          {emailError && (
            <div className="booking-text-xs booking-mt-xs" style={{ color: 'var(--color-danger)' }}>
              ⚠️ {emailError}
            </div>
          )}
        </div>

        {false && (
          <div className="booking-form-group">
            <div className="booking-label" style={{ marginBottom: 'var(--spacing-sm)' }}>
              {t('booking.client.additional')}
            </div>
            <label className="booking-label" style={{ display: 'block', marginBottom: 4 }} title={t('booking.client.genderOptional')}>
              {t('booking.client.genderOptional')}
            </label>
            <select
              value={clientGender}
              onChange={(e) => onGenderChange((e.target.value || '') as 'male' | 'female' | '')}
              className="booking-input"
            >
              <option value="">{t('booking.client.genderNotSelected')}</option>
              <option value="male">{t('booking.client.genderMale')}</option>
              <option value="female">{t('booking.client.genderFemale')}</option>
            </select>
            <div style={{ marginTop: 'var(--spacing-md)' }}>
              <label className="booking-label" style={{ display: 'block', marginBottom: 4 }} title={t('booking.client.birthDateOptional')}>
                {t('booking.client.birthDateOptional')}
              </label>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder={t('booking.client.birthDateDD')}
                  value={clientBirthDate.day}
                  onChange={(e) =>
                    onBirthDateChange({ ...clientBirthDate, day: e.target.value.replace(/\D/g, '').slice(0, 2) })
                  }
                  className="booking-input"
                  style={{ width: '4rem', textAlign: 'center' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder={t('booking.client.birthDateMM')}
                  value={clientBirthDate.month}
                  onChange={(e) =>
                    onBirthDateChange({ ...clientBirthDate, month: e.target.value.replace(/\D/g, '').slice(0, 2) })
                  }
                  className="booking-input"
                  style={{ width: '4rem', textAlign: 'center' }}
                />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder={t('booking.client.birthDateYYYY')}
                  value={clientBirthDate.year}
                  onChange={(e) =>
                    onBirthDateChange({ ...clientBirthDate, year: e.target.value.replace(/\D/g, '').slice(0, 4) })
                  }
                  className="booking-input"
                  style={{ width: '5rem', textAlign: 'center' }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="booking-form-group">
          <label className="booking-label">{t('booking.client.commentOptional')}</label>
          <textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            rows={4}
            className="booking-textarea"
          />
        </div>
      </form>
    </div>
  );
}
